import { useQuery } from '@apollo/client/react'
import React, { FC, useCallback, useContext } from 'react'
import { PileNode, StringPaginationType } from '@src/generated/schema'
import { Pile } from '@src/graphql/Pile'
import debounce from 'lodash.debounce'
import { MobileSearchSelect } from '@src/components/Mobile/Elements/SearchSelect'
import { NetworkContext } from '@src/components/Mobile/Providers/Network'
import { MobileSelectProps } from '@src/components/Mobile/Elements/SearchSelect/SearchSelect'
import { getUniqueList } from '@src/helpers/Logic/Logic'

export interface MobileTimberSelectProps extends Omit<MobileSelectProps, 'options'> {
  piles?: PileNode[]
}

export const MobileTimberSelect: FC<MobileTimberSelectProps> = ({ piles, ...props }) => {
  const { isOnline } = useContext(NetworkContext)
  const { data, loading, fetchMore, refetch } = useQuery<{ timbers: StringPaginationType }>(Pile.timbers, {
    variables: {
      pageSize: 20,
    },
  })
  const { timbers: { page, strings, hasNext } = { page: 0, strings: [], hasNext: false } } = data || {}

  const handleSearch = useCallback(
    debounce((filterValue: string) => {
      refetch({ filterValue, pageSize: 20 })
    }, 500),
    [],
  )

  const handlePopupScroll = () => {
    const previousPage: number = page || 0

    if (!hasNext) return

    fetchMore({
      variables: {
        page: previousPage + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const newPage = fetchMoreResult?.timbers?.page || 0
        const oldPage = prev?.timbers?.page || 0

        if (!fetchMoreResult || newPage <= oldPage) return prev

        const newEdges = fetchMoreResult.timbers.strings
        const previousEdges = prev?.timbers?.strings || []

        return newEdges?.length
          ? {
              timbers: {
                page: fetchMoreResult.timbers.page,
                pages: fetchMoreResult.timbers.pages,
                hasNext: fetchMoreResult.timbers.hasNext,
                hasPrevious: fetchMoreResult.timbers.hasPrevious,
                __typename: prev.timbers.__typename,
                strings: [...previousEdges, ...newEdges],
              },
            }
          : prev
      },
    })
  }

  return (
    <MobileSearchSelect
      {...props}
      options={
        isOnline
          ? strings?.map((timber) => ({
              id: timber,
              title: timber,
              value: timber,
            }))
          : getUniqueList(piles?.map((p) => p.timber)).map((timber) => ({
              id: timber,
              value: timber,
              title: timber,
            }))
      }
      loading={loading}
      multi
      onSearch={handleSearch}
      onLoadMore={handlePopupScroll}
      allowNoSelection
    />
  )
}
